import { useUser } from "@/lib/hooks/use-user";
import { UserRole } from "@/lib/interfaces/user";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/ui-components/ui/tooltip";
import { BarChart, Calendar, Contact, Euro, Handshake, HelpCircle, LogOut, Receipt, Settings, User, CheckSquare, Layers, Users, History } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

interface SidebarItemI {
  title: string;
  icon: JSX.Element;
  link: string;
  end?: boolean;
  isActive?: boolean;
}

const sidebarSales = {
  title: "Verkäufe",
  icon: <Euro />,
  link: "/sales",
};

const sidebarLogout = {
  title: "Ausloggen",
  icon: <LogOut />,
  link: "/logout",
  end: true,
};

const sidebarSettings = {
  title: "Einstellungen",
  icon: <Settings />,
  link: "/settings",
  end: true,
};

const sidebarDashboard = {
  title: "Dashboard",
  icon: <BarChart />,
  link: "/",
};

const sidebarDeals = {
  title: "Deals",
  icon: <Handshake />,
  link: "/deals",
};

const sidebarPipelines = {
  title: "Pipelines",
  icon: <Layers />,
  link: "/pipelines",
};

const sidebarUsers = {
  title: "Benutzer",
  icon: <Users />,
  link: "/users",
};

const sidebarUserHistory = {
  title: "Nutzer Aktivität",
  icon: <History />,
  link: "/user-history",
};

const sidebarCalendar = {
  title: "Kalender",
  icon: <Calendar />,
  link: "/calendar",
};

const sidebarTasks = {
  title: "Aufgaben",
  icon: <CheckSquare />,
  link: "/tasks",
};

const sidebarContact = {
  title: "Kontakte",
  icon: <Contact />,
  link: "/contacts",
};

const sidebarPersonal = {
  title: "Mein Account",
  icon: <User />,
  link: "/personal",
  end: true,
};

const sidebarInvoices = {
  title: "Rechnungen",
  icon: <Receipt />,
  link: "/invoices",
};

const sidebarHelp = {
  title: "Hilfe",
  icon: <HelpCircle />,
  link: "/help",
  end: true,
};

const Sidebar = () => {
  const { user } = useUser();

  let sidebarItems: SidebarItemI[] = [];

  if (user) {
    sidebarItems.push(sidebarDashboard);
    
    // Add pipelines and users for Admin/SuperAdmin
    if (user.role === UserRole.Admin || user.role === UserRole.SuperAdmin) {
      sidebarItems.push(sidebarPipelines);
      sidebarItems.push(sidebarUsers);
      sidebarItems.push(sidebarUserHistory);
    }
    
    sidebarItems.push(sidebarDeals);
    sidebarItems.push(sidebarCalendar);
    sidebarItems.push(sidebarTasks);
    sidebarItems.push(sidebarContact);
    sidebarItems.push(sidebarSales);

    if (user.role === UserRole.Admin || user.role === UserRole.SuperAdmin) {
      sidebarItems.push(sidebarInvoices);
    }

    sidebarItems.push(sidebarHelp);
    sidebarItems.push(sidebarPersonal);

    if (user.role === UserRole.Admin || user.role === UserRole.SuperAdmin) {
      sidebarItems.push(sidebarSettings);
    }
    
    sidebarItems.push(sidebarLogout);
  }

  const location = useLocation();

  const checkIsActive = (link: string) => {
    // Special handling for home route
    if (link === "/") {
      // Check if any other routes match first
      const otherRouteActive = sidebarItems
        .filter((item) => item.link !== "/")
        .some((item) => location.pathname.startsWith(item.link));

      // Only show home as active if no other routes match and we're at root
      return !otherRouteActive && location.pathname === "/";
    }

    // For all other routes, check if the pathname starts with the link
    return location.pathname.startsWith(link);
  };

  return (
    <div className="flex flex-col fixed h-full z-20 items-center bg-[#100000] text-white w-14 pt-3">
      <div className="relative px-2 max-w-28">
        <Link to="/" className="flex gap-2 flex-col">
          <img src="/logo.png" alt="logo" />
        </Link>
      </div>
      <nav className="flex flex-col gap-2 mt-6">
        {sidebarItems
          .filter((item) => !item.end)
          .map((item, index) => (
            <SidebarItem key={item.link} {...item} isActive={checkIsActive(item.link)} />
          ))}
      </nav>
      <nav className="flex flex-col gap-1 mt-auto mb-3">
        {sidebarItems
          .filter((item) => item.end)
          .map((item) => (
            <SidebarItem key={item.link} {...item} isActive={checkIsActive(item.link)} />
          ))}
      </nav>
    </div>
  );
};

const SidebarItem = ({ icon, link, isActive, title }: SidebarItemI) => {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger>
          <Link
            to={link}
            className={`w-[40px] h-[40px] flex justify-center items-center hover:bg-[#c5260a] rounded-sm ${
              isActive ? "bg-[#b13722]" : ""
            }`}
          >
            {icon}
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right" className="bg-[#c5260a] text-white">
          <p>{title}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default Sidebar;
